import { createRef, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { format, parse, isValid } from "date-fns";
import * as XLSX from "xlsx";
import CSVReader from "../../../../utils/CSVReader";

import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Menu } from "primereact/menu";
import { Tooltip } from "primereact/tooltip";

import { useInfo } from "../../../../context/infoContext";
import { isErroGeracaoSuspensa, isGeracaoBoletoSuspensa, MENSAGEM_GERACAO_SUSPENSA } from "../../../../utils/geracaoBoleto";
import { ApiAdimplencia } from "../../../../services/api";
import { getLocal } from "../../../../utils/localStorageOps";

import { FaDownload, FaFile, FaTable, FaUpload } from "react-icons/fa";
import { GiCog } from "react-icons/gi";
import ExemploXLSX from "../../../../assets/carga_adimplencia.xlsx";
import AppCard from "../../../../components/appCard";
import ImportacaoErrorDialog from "../../../../components/dashboard/adimplencias/importacoes/errorDialog";
import { Download, Errors, ReportButton, Resume } from "../../../../styles/adimplencia/importacoes";
import { DataTablePague } from "../../../../styles/primeReactConfig";
import { validarCpfCnpj } from "../../../../utils/validacoes";

function Importacao() {
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState([]);
  const [dadosErros, setDadosErros] = useState([]);
  const [currentKeys, setCurrentKeys] = useState([]);
  const [upload, setUpload] = useState([]);
  const [erros, setErros] = useState([]);
  const [showErrorReport, setShowErrorReport] = useState(false);
  const [reportInfo, setReportInfo] = useState();

  const [loads, setLoads] = useState([]);
  const [rows, setRows] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [first, setFirst] = useState(0);
  const [page, setPage] = useState(0);
  const [typeOfFile, setTypeOfFile] = useState("");
  const [rowCurrent, setRowCurrent] = useState([]);

  const token = getLocal("token");
  const menu = createRef();

  const { credorData } = useInfo();
  const geracaoSuspensa = isGeracaoBoletoSuspensa(credorData);

  const DEFAULT_MAX_VALOR_COBRANCA = 10000;

  const MAX_VALOR_POR_CREDOR = {
    "6e39ee2c-55e6-4e3b-bab5-623e377dd56b": 25000, // DEKRA TESTING
  };

  /**
   * Retorna o teto máximo permitido para o credor atual.
   * @param {string | undefined | null} credorId
   * @returns {number}
   */
  function getMaxValorCobranca(credorId) {
    if (!credorId) return DEFAULT_MAX_VALOR_COBRANCA;
    return MAX_VALOR_POR_CREDOR[credorId] ?? DEFAULT_MAX_VALOR_COBRANCA;
  }

  useEffect(() => {
    getAllLoads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credorData, page]);

  // =========================
  // Helpers
  // =========================

  /**
   * Verifica se uma célula está vazia (null/undefined/string vazia).
   * @param {unknown} value
   * @returns {boolean}
   */
  function isEmptyCell(value) {
    return value === null || value === undefined || String(value).trim() === "";
  }

  /**
   * Remove máscara deixando apenas dígitos.
   * @param {unknown} value
   * @returns {string}
   */
  function removeMask(value) {
    if (value === null || value === undefined) return "";
    return String(value).replace(/\D/g, "");
  }

  /**
   * Faz parse de número no padrão BR, tolerando "R$" e milhar com ponto.
   * @param {unknown} value
   * @returns {number}
   */
  function parseBRNumber(value) {
    if (value === null || value === undefined) return NaN;
    if (typeof value === "number") return value;

    let str = String(value).trim();
    if (!str) return NaN;

    str = str.replace(/\s/g, "");
    str = str.replace(/^R\$\s?/, "");
    // 1.234,56 -> 1234.56
    str = str.replace(/\./g, "").replace(",", ".");

    const num = Number(str);
    return Number.isFinite(num) ? num : NaN;
  }

  /**
   * Converte date do Excel (serial), Date, ou string em Date.
   * @param {unknown} value
   * @returns {Date|null}
   */
  function parseAnyDate(value) {
    if (value instanceof Date && isValid(value)) return value;

    if (typeof value === "number" && Number.isFinite(value)) {
      // Excel serial
      const base = new Date(1899, 11, 30);
      const d = new Date(base.getTime() + value * 86400 * 1000);
      return isValid(d) ? d : null;
    }

    if (typeof value === "string") {
      const str = value.trim();
      if (!str) return null;

      // yyyy-MM-dd
      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        const d = new Date(`${str}T00:00:00`);
        return isValid(d) ? d : null;
      }

      // dd/MM/yyyy
      const d1 = parse(str, "dd/MM/yyyy", new Date());
      if (isValid(d1)) return d1;

      // dd-MM-yyyy
      const d2 = parse(str, "dd-MM-yyyy", new Date());
      if (isValid(d2)) return d2;

      // fallback
      const d3 = new Date(str);
      return isValid(d3) ? d3 : null;
    }

    return null;
  }

  /**
   * Verifica se a data informada está no futuro (comparando por dia).
   * @param {unknown} value
   * @returns {boolean}
   */
  function isFutureDate(value) {
    const d = parseAnyDate(value);
    if (!d) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const dt = new Date(d);
    dt.setHours(0, 0, 0, 0);

    return dt.getTime() > today.getTime();
  }

  /**
   * Valida CPF/CNPJ tolerando zeros à esquerda removidos pelo Excel.
   * @param {unknown} raw
   * @returns {{ valido: boolean, tipo: 'CPF'|'CNPJ'|null, digits: string }}
   */
  function checkDocumentValid(raw) {
    const r = validarCpfCnpj(raw);
    return { valido: r.valido, tipo: r.tipo, digits: r.digits };
  }

  /**
   * Valida se valor parece um nome (letras/acentos/espaços).
   * @param {unknown} value
   * @returns {boolean}
   */
  function isNameText(value) {
    if (isEmptyCell(value)) return false;
    // aceita letras (inclui acentos), espaços e alguns separadores comuns
    return /^[A-Za-zÀ-ÖØ-öø-ÿ\s.'-]+$/.test(String(value).trim());
  }

  /**
   * Verifica maioridade (>= 18 anos) a partir da data de nascimento.
   * @param {unknown} dataNascimento
   * @returns {boolean}
   */
  function verificarMaioridadePorData(dataNascimento) {
    const hoje = new Date();

    let dn = null;
    if (dataNascimento instanceof Date) {
      dn = dataNascimento;
    } else {
      const parsed = parseAnyDate(dataNascimento);
      dn = parsed;
    }

    if (!dn) return false;

    let idade = hoje.getFullYear() - dn.getFullYear();
    const m = hoje.getMonth() - dn.getMonth();
    if (m < 0 || (m === 0 && hoje.getDate() < dn.getDate())) {
      idade--;
    }

    return idade >= 18;
  }

  // =========================
  // Import file handler
  // =========================
  function handleImportFile(e, file) {
    setUpload(file);
    const data = [];

    for (let i = 0; i < e.length; i++) {
      data.push(e[i].data);
    }
    data.pop();

    const knowType = file.name.split(".");
    const size = knowType.length;
    const fileType = knowType[size - 1];
    setTypeOfFile(fileType);

    if (fileType === "xlsx" || fileType === "xls") {
      const reader = new FileReader();
      const rABS = !!reader.readAsBinaryString;

      reader.onload = (ev) => {
        const bstr = ev.target.result;
        const wb = XLSX.read(bstr, {
          type: rABS ? "binary" : "array",
          bookVBA: true,
          cellNF: true,
          cellDates: true, // mantém datas como Date quando possível
        });

        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const dataArray = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", blankrows: false });
        let dataXLSX = [["linha"]];
        for (let j = 1; j < dataArray.length; j++) {
          dataXLSX[j] = [j];
          dataXLSX[j] = dataXLSX[j].concat(dataArray[j]);
        }
        dataXLSX[0] = dataXLSX[0].concat(dataArray[0]);

        if (dataXLSX.length === 0) {
          toast.error("Arquivo Vazio!");
        } else {
          verifyData(dataXLSX, fileType);
        }
      };

      if (rABS) {
        reader.readAsBinaryString(file);
      } else {
        reader.readAsArrayBuffer(file);
      }
    } else if (fileType === "csv" || fileType === "CSV") {
      if (data[0] === undefined) {
        toast.error("Arquivo Vazio!");
      } else {
        let dataCSV = [["linha"]];
        dataCSV[0] = dataCSV[0].concat(data[0]);
        for (let j = 1; j < data.length; j++) {
          dataCSV[j] = [j];
          dataCSV[j] = dataCSV[j].concat(data[j]);
        }
        verifyData(dataCSV, fileType);
      }
    } else {
      toast.error("Tipo de arquivo inválido, favor colocar um arquivo (.csv, .xlsx ou .xls) ");
    }
  }

  // =========================
  // Verify Data (NOVO PADRÃO)
  // =========================
  function verifyData(arr, filetype) {
    let result = [];
    let arrayErros = [];
    let errosRowsSet = new Set();
    let dadosErrosLocal = [];
    let raw = arr;

    const maxValorCobranca = getMaxValorCobranca(credorData?.credor_id);

    // headers
    const keys = raw[0].map((item) => (item ?? "").toString().trim());

    // Para XLSX: normaliza Date -> dd/MM/yyyy (só para validação/preview)
    if (filetype === "xlsx" || filetype === "xls") {
      for (let j = 1; j < raw.length; j++) {
        for (let k = 0; k < raw[j].length; k++) {
          if (raw[j][k] instanceof Date && isValid(raw[j][k])) {
            raw[j][k] = format(raw[j][k], "dd/MM/yyyy");
          }
        }
      }
    }

    raw.forEach((listas) => {
      const obj = {};
      for (let i = 0; i < keys.length; i++) {
        obj[keys[i]] = listas[i];
      }
      result.push(obj);
    });

    result.shift(); // remove header

    // Colunas obrigatórias do NOVO padrão
    const colunasObrigatorias = [
      "documento_devedor",
      "nome_devedor",
      "email_devedor",
      "telefone_devedor",
      "data_nascimento",
      "descricao_debito",
      "primeiro_vencimento",
      "valor",
      "dia_vencimento",
      "endereco_devedor",
      "numero_devedor",
      "bairro_devedor",
      "cidade_devedor",
      "uf_devedor",
      "cep_devedor",
    ];

    if (result.length === 0) {
      toast.error("Arquivo vazio!");
      arrayErros.push({
        row: 0,
        column: 0,
        description: "O arquivo não contém nenhuma cobrança!",
      });
      setDados([]);
      setErros(arrayErros);
      setDadosErros([]);
      setCurrentKeys(keys);
      return;
    }

    // detecta colunas faltando
    const headerKeys = Object.keys(result[0]);
    for (const col of colunasObrigatorias) {
      if (!headerKeys.includes(col)) {
        arrayErros.push({
          row: 0,
          column: 0,
          description: `Arquivo sem a coluna obrigatória ${col}`,
        });
      }
    }

    // valida linha a linha
    for (let i = 0; i < result.length && arrayErros.length < 200; i++) {
      const rowObj = result[i];
      const rowKeys = Object.keys(rowObj);
      const rowValues = Object.values(rowObj);
      const rowNumber = i + 1;

      const markRowError = () => {
        if (!errosRowsSet.has(rowNumber)) {
          errosRowsSet.add(rowNumber);
          dadosErrosLocal.push(rowObj);
        }
      };

      for (let cellIndex = 0; cellIndex < rowValues.length; cellIndex++) {
        const columnName = rowKeys[cellIndex];
        const cell = rowValues[cellIndex];

        // obrigatório vazio
        if (colunasObrigatorias.includes(columnName) && isEmptyCell(cell)) {
          markRowError();
          arrayErros.push({
            row: rowNumber,
            column: columnName,
            description: "célula obrigatória vazia",
          });
          continue;
        }

        // valida documento (CPF/CNPJ) - tolera zeros perdidos no Excel
        if (columnName === "documento_devedor" && !isEmptyCell(cell)) {
          const r = checkDocumentValid(cell);

          if (!r.valido) {
            markRowError();
            arrayErros.push({
              row: rowNumber,
              column: columnName,
              description:
                "Você deve inserir um documento válido (CPF/CNPJ). Verifique se a coluna não foi convertida para número no Excel.",
            });
          } else {
            // melhora preview: salva valor normalizado (com zeros à esquerda quando aplicável)
            rowObj[columnName] = r.digits;
          }
        }

        // valida nome
        if (columnName === "nome_devedor" && !isEmptyCell(cell)) {
          if (!isNameText(cell)) {
            markRowError();
            arrayErros.push({
              row: rowNumber,
              column: columnName,
              description: "o nome deve conter apenas letras",
            });
          }
        }

        // valida maioridade
        if (columnName === "data_nascimento" && !isEmptyCell(cell)) {
          if (!verificarMaioridadePorData(cell)) {
            markRowError();
            arrayErros.push({
              row: rowNumber,
              column: columnName,
              description: "O devedor deve ser maior de idade.",
            });
          }
        }

        // valida primeiro vencimento (deve ser > hoje)
        if (columnName === "primeiro_vencimento" && !isEmptyCell(cell)) {
          if (!isFutureDate(cell)) {
            markRowError();
            arrayErros.push({
              row: rowNumber,
              column: columnName,
              description: "o primeiro vencimento deve estar no futuro e no formato dd/mm/aaaa ou yyyy-mm-dd",
            });
          }
        }

        // valida valor
        if (columnName === "valor" && !isEmptyCell(cell)) {
          const v = parseBRNumber(cell);
          if (!Number.isFinite(v) || v <= 0) {
            markRowError();
            arrayErros.push({
              row: rowNumber,
              column: columnName,
              description: "o valor da cobrança deve ser numérico e maior que zero",
            });
            continue;
          }

          if (v > maxValorCobranca) {
            markRowError();
            arrayErros.push({
              row: rowNumber,
              column: columnName,
              description: `o valor da cobrança não pode ultrapassar R$ ${maxValorCobranca.toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`,
            });
            continue;
          }
        }

        // valida dia vencimento 1..31
        if (columnName === "dia_vencimento" && !isEmptyCell(cell)) {
          const dv = parseInt(removeMask(cell), 10);
          if (!Number.isFinite(dv) || dv < 1 || dv > 31) {
            markRowError();
            arrayErros.push({
              row: rowNumber,
              column: columnName,
              description: "O dia do vencimento deve ser um inteiro entre 1 e 31.",
            });
          }
        }

        // uf deve ter 2 letras
        if (columnName === "uf_devedor" && !isEmptyCell(cell)) {
          const uf = String(cell).trim();
          if (uf.length !== 2) {
            markRowError();
            arrayErros.push({
              row: rowNumber,
              column: columnName,
              description: "UF deve conter 2 caracteres (ex: PA, SP).",
            });
          }
        }

        // cep deve ter pelo menos 8 dígitos
        if (columnName === "cep_devedor" && !isEmptyCell(cell)) {
          const cep = removeMask(cell);
          if (cep.length < 8) {
            markRowError();
            arrayErros.push({
              row: rowNumber,
              column: columnName,
              description: "CEP inválido (mínimo 8 dígitos).",
            });
          }
        }
      }
    }

    setCurrentKeys(keys);
    setDados(result);
    setErros(arrayErros);
    setDadosErros(dadosErrosLocal);

    if (arrayErros.length === 0) {
      toast.success("Arquivo pronto para upload!");
    }
  }

  function handleReport(e) {
    setReportInfo(e);
    setShowErrorReport(true);
  }

  function onPage(event) {
    const startIndex = parseInt(event.first);
    setRows(event.rows);
    setPage(event.page);
    setFirst(startIndex);
  }

  const base64toBlob = (data, mimetype_arquivo) => {
    const pdfContentType = mimetype_arquivo;
    const base64WithoutPrefix = data.substr(`data:${pdfContentType};base64,`.length);

    const bytes = atob(base64WithoutPrefix);
    let length = bytes.length;
    let out = new Uint8Array(length);

    while (length--) {
      out[length] = bytes.charCodeAt(length);
    }

    return new Blob([out], { type: pdfContentType });
  };

  function handleSelection(e) {
    if (!menu.current) {
    } else menu.current.toggle(e);
  }
  function handleSelectAction(rowData) {
    setRowCurrent(rowData);
  }

  async function handleUploadFile() {
    if (geracaoSuspensa) {
      toast.warning(MENSAGEM_GERACAO_SUSPENSA);
      return;
    }

    setLoading(true);
    const formData = new FormData();
    let blobType = "";

    if (typeOfFile === "xls" || typeOfFile === "xlsx") blobType = upload.type;
    else blobType = "text/csv";

    formData.append("file", new Blob([upload], { type: blobType }), upload.name);

    formData.append(
      "cargaData",
      JSON.stringify({
        extensao_arquivo: typeOfFile,
        mimetype_arquivo: blobType,
        credor_id: credorData.credor_id,
      })
    );

    const config = { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } };

    try {
      await ApiAdimplencia.post("/cargas", formData, config);
      toast.success("Arquivo importado com sucesso!");
      getAllLoads();
    } catch (err) {
      // Geração suspensa já é avisada pelo interceptor da ApiAdimplencia
      if (!isErroGeracaoSuspensa(err)) toast.error("Erro ao importar arquivo!");
    } finally {
      setLoading(false);
    }
  }

  async function getAllLoads() {
    setLoading(true);
    try {
      const { data } = await ApiAdimplencia.get(`cargas?limit=${rows}&page=${page}`);
      setLoads(data.dados.cargas);
      setTotalRecords(data.dados.count);
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  }

  const abrirArquivo = async (carga_id) => {
    try {
      toast.info("Abrindo arquivo");
      const { data } = await ApiAdimplencia.get(`cargas/${carga_id}/arquivo`);

      if (data.dados?.base64_pdf) {
        const base64 = `data:${data.dados.mimetype_arquivo};base64,${data.dados.base64_pdf}`;
        const url = URL.createObjectURL(base64toBlob(base64, data.dados.mimetype_arquivo));
        window.open(url);
      }
    } catch (error) {
      toast.error("Houve um erro ao tentar abrir o arquivo.");
    }
  };

  function handleRemove() {
    setDados([]);
    setErros([]);
    setDadosErros([]);
    setCurrentKeys([]);
  }

  const dynamicColumns = currentKeys.map((col) => {
    return <Column style={{ width: "350px" }} key={col} field={col} header={col} />;
  });

  const statusBodyTemplate = (e) => {
    const typeMap = {
      INVÁLIDO: "bg-red-100 text-red-800 dark:bg-red-800/30 dark:text-red-500 ring-red-500 ring-1 ring-inset !rounded-md",
      aguardando: "text-blue-800 dark:bg-blue-800/30 dark:text-blue-500 ring-blue-500 ring-1 ring-inset !rounded-md",
      CARREGADA: "bg-teal-100 text-teal-800 dark:bg-teal-800/30 dark:text-teal-500 ring-teal-500 ring-1 !rounded-md",
      "CARREGADA PARCIALMENTE":
        "bg-yellow-100 text-yellow-800 dark:bg-yellow-800/30 dark:text-yellow-500 ring-yellow-500 ring-1 !rounded-md",
    };

    const type = typeMap[e.status] || "bg-gray-100 text-gray-800 dark:bg-white/10 dark:text-white";

    return (
      <div className={`inline-flex items-center justify-center rounded-sm ${type} px-2 py-1 text-sm font-medium w-fit`}>
        <p>{e.status}</p>
      </div>
    );
  };

  const acoesLista = [
    {
      label: "Baixar Aquivo",
      icon: "pi pi-cloud-download",
      value: "GF",
      command: () => {
        abrirArquivo(rowCurrent.carga_id);
      },
    },
  ];

  const actionTemplate = (rowData) => {
    return (
      <div className="w-full flex justify-center">
        <Menu model={acoesLista} popup ref={menu} />
        <Button
          icon={<GiCog className="text-2xl" />}
          pt={{
            root: `hover:dark:bg-emerald-300 hover:bg-green-600 
            dark:bg-emerald-500 bg-green-500
            h-10 w-10 rounded-full
            flex justify-center items-center
            text-white dark:text-gray-900 
            transition duration-300`,
            label: "absolute",
          }}
          onClick={(e) => {
            handleSelection(e);
            handleSelectAction(rowData);
          }}
        />
      </div>
    );
  };

  function actionBody(e) {
    if (e.situacao.situacao_nome === "CARREGADA" || e.situacao.situacao_nome === "CARREGADA PARCIALMENTE")
      return (
        <div className="w-full flex justify-center">
          <ReportButton onClick={() => handleReport(e)}>
            <FaFile className="text-xl text-white" />
          </ReportButton>
        </div>
      );
    else if (e.situacao.situacao_nome === "INVÁLIDO")
      return (
        <div className="w-full flex justify-center">
          <Tooltip position="bottom" target=".disabled-button" />
          <span className="disabled-button" data-pr-tooltip="Arquivo INVÁLIDO">
            <ReportButton disabled>
              <FaFile className="text-xl text-white" />
            </ReportButton>
          </span>
        </div>
      );
    else
      return (
        <div className="w-full flex justify-center">
          <Tooltip position="bottom" target=".disabled-button" />
          <span className="disabled-button" data-pr-tooltip="Processamento ainda não finalizado">
            <ReportButton disabled>
              <FaFile className="text-xl text-white" />
            </ReportButton>
          </span>
        </div>
      );
  }

  const headerHistorico = (
    <Resume>
      <span>
        <FaTable />
      </span>
      <h5>
        <b>Histórico de Cargas</b>
      </h5>
    </Resume>
  );

  return (
    <div className="flex flex-col">
      {showErrorReport && <ImportacaoErrorDialog close={() => setShowErrorReport(false)} reportInfo={reportInfo} />}

      <AppCard className="w-full">
        <div className="flex flex-wrap p-3 justify-between">
          <div className="md:w-1/2 w-full mb-4 md:mb-0">
            <p className="text-xl font-bold">Importações Boleto Inteligente</p>
            <p>
              Importe a lista <b>CSV, XLSX ou XLS</b> em <b>UTF-8</b>
            </p>
            <p>Ela deve conter todos os campos preenchidos</p>

            {erros.length > 0 && (
              <Errors>
                <div className="title">
                  <i className="pi pi-times-circle" style={{ fontSize: "2em" }} />
                  <label style={{ color: "#932f2f", fontWeight: "bold" }}>Encontramos erros nas seguintes posições:</label>
                </div>
                <div className="error">
                  {erros.map((item, idx) => (
                    <span key={idx} style={{ color: "#a64949", margin: "0 24px 8px 0" }}>
                      {`- Linha ${item.row}, coluna de ${item.column}, Descrição: ${item.description} `}
                    </span>
                  ))}
                </div>
              </Errors>
            )}

            <Download href={ExemploXLSX} download="Exemplo.xlsx">
              <div style={{ fontSize: "20px" }}>
                <FaDownload />
              </div>
              <p className="mx-2">Arquivo de Exemplo</p>
            </Download>
            <p className="text-pink-600 dark:text-yellow-400 font-bold mt-2">
              *Um novo padrão de importação está sendo usado, baixe acima
            </p>
            <p className="text-pink-600 dark:text-yellow-400 font-bold mt-2">*PADRÃO NOVO, atualizado em 25/02/2026</p>
          </div>

          <div className="md:w-1/2 w-full flex flex-row justify-center sm:justify-end items-center">
            <div className="flex flex-col items-center gap-2">
              <span>
                Formatos de Arquivos aceitos:
                <br />
                <b>'.xlsx, .xls, .csv'</b>
              </span>
              <CSVReader
                sendData={(e, file) => {
                  handleImportFile(e, file);
                }}
                rejectData={(file) => toast.error(`Tipo de arquivo não suportado: .${file}`)}
                removeFile={handleRemove}
              />
              <Button
                onClick={handleUploadFile}
                disabled={geracaoSuspensa || !(dados.length > 0 && erros.length === 0)}
                tooltip={geracaoSuspensa ? "Geração de boletos suspensa" : undefined}
                tooltipOptions={{ showOnDisabled: true, position: "top" }}
              >
                <FaUpload />
                <span className="ml-3">Enviar</span>
              </Button>
            </div>
          </div>
        </div>

        {dados.length > 0 && (
          <AppCard className="w-full">
            <h5>
              <b>Pré-visualização de Planilha</b>
            </h5>
            <DataTable
              value={dadosErros}
              scrollable
              scrollHeight="60vh"
              emptyMessage="Arquivo pronto para upload."
              pt={DataTablePague}
            >
              {dynamicColumns}
            </DataTable>
          </AppCard>
        )}
      </AppCard>

      <AppCard className="w-full !p-0 mt-4">
        <DataTable
          header={headerHistorico}
          value={loads}
          rows={rows}
          totalRecords={totalRecords}
          first={first}
          onPage={onPage}
          loading={loading}
          pt={DataTablePague}
          paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          currentPageReportTemplate="Exibindo {first} de {last} do total de {totalRecords}"
          emptyMessage="Nenhuma carga encontrada. "
          size="small"
          scrollable
          paginator
          lazy
        >
          <Column headerClassName="md:flex justify-center p-0" header="Ações" body={(rowData) => actionTemplate(rowData)} />
          <Column
            field="carga_status"
            header="Situação"
            body={(rowData) => statusBodyTemplate({ status: rowData.situacao.situacao_nome, label: rowData.situacao_label })}
          />
          <Column field="created_at" header="Data Upload" body={(rowData) => format(new Date(rowData.created_at), "dd/MM/yyyy")} />
          <Column headerClassName="md:flex justify-center" header="Relatório" body={(rowData) => actionBody(rowData)} />
        </DataTable>
      </AppCard>
    </div>
  );
}

export default Importacao;
